import { DEFAULT_LOCALE, LOCALES_LIST } from '@/constants/locales';
import { createI18n, type I18n } from 'vue-i18n';

import merge from 'lodash/merge';
import { getCookie } from '@/utils/cookies';
import { Cookie } from '@/constants/storage';

export const getLanguageCode = () => {
	const language = getCookie(Cookie.SAPI_LANGUAGE);

	if (language && LOCALES_LIST.find((lang) => lang.code === language)) {
		return language;
	}

	return DEFAULT_LOCALE;
};

const locale = getLanguageCode();

let hasLoadedMessages = false;
// first we initialise the messages for each locale as empty objects
// this will later help us reactively update message values
const messages = LOCALES_LIST.reduce(async (accumulator, currentLang) => {
	return {
		...accumulator,
		[currentLang.code]: {},
	};
}, {});

export const i18n = createI18n({
	legacy: false,
	warnHtmlMessage: false,
	locale,
	fallbackLocale: DEFAULT_LOCALE,
	messages,
	missing: (_, key) => {
		if (
			hasLoadedMessages &&
			(process.env.VITE_SCORO_ENV === 'development' ||
				process.env.VITE_SCORO_ENV === 'staging')
		) {
			return `lang var[${key}] missing!`;
		}

		return '';
	},
});

// lazy load messages for a given locale
export const loadLanguageMessages = async (
	locale: string,
	setMessage = i18n.global.setLocaleMessage
) => {
	const messages = await import(`../generated/locales/${locale}.js`);
	setMessage(locale, messages.default);
	hasLoadedMessages = true;
};

// code splitting all locales into separate files and loading them into i18n
export const loadAllLanguages = () => {
	LOCALES_LIST.map(async (currentLang) => {
		const messages = await import(
			`../generated/locales/${currentLang.file}.js`
		);
		i18n.global.setLocaleMessage(currentLang.code, messages.default);
	});
};

// lazy loading current active language
export const loadActiveLanguage = () => {
	return loadLanguageMessages(locale);
};

// Change the UI language
export const changeLanguage = (locale: string) => {
	loadLanguageMessages(locale);
	i18n.global.locale.value = locale;
};

/**
 * Load the custom translations for the current site.
 *
 * Assume that a cookie is set specifying a path to the custom translations JSON
 * file.
 */
export const loadCustomTranslations = async (i18n: I18n) => {
	const customTranslationsUrl = getCookie(Cookie.SAPI_CUSTOM_TRANSLATIONS);
	if (!customTranslationsUrl) {
		return;
	}

	// The URL in the cookie is URL-encoded
	const url = decodeURIComponent(customTranslationsUrl);

	const res = await fetch(url).catch(() => undefined);
	if (!res || !res.ok) {
		return;
	}

	const translations = await res.json();
	Object.entries(translations).forEach(([lang, langvars]) => {
		const existingMessages = i18n.global.getLocaleMessage(lang);
		i18n.global.setLocaleMessage(lang, merge(existingMessages, langvars));
	});
};
